import { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import './TestCaseDetail.css';
import './CreateTestCaseModal.css';
import { AUTOMATION_STATUS_OPTIONS, FIELD_OPTIONS, httpErrorMessage } from './testCaseConstants';
import FolderPicker from './FolderPicker';
import StepsEditor from './StepsEditor';
import LabelsPicker from './LabelsPicker';

const PROJECT_TYPE_OPTIONS = ['Web', 'Mobile'];

const EMPTY_FORM = {
  summary: '',
  description: '',
  precondition: '',
  automationStatus: '',
  testPyramid: '',
  type: '',
  priority: '',
  unit: '',
  labels: [],
  projectType: [],
  techStack: '',
};

export default function CreateTestCaseModal({ isOpen, onClose, onCreated, projects, defaultProject, folderTree: initialFolderTree }) {
  const [formData, setFormData] = useState({ ...EMPTY_FORM, project: defaultProject ?? '' });
  const [steps, setSteps] = useState([]);
  const [folderPath, setFolderPath] = useState('');
  const [folderTree, setFolderTree] = useState(initialFolderTree ?? null);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [availableLabels, setAvailableLabels] = useState([]);
  const fieldOptions = FIELD_OPTIONS;

  useEffect(() => {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/labels`)
      .then(res => setAvailableLabels(res.data))
      .catch(() => {});
  }, []);

  const fetchFolderTree = useCallback(async (project) => {
    if (!project) { setFolderTree(null); return; }
    setLoadingFolders(true);
    try {
      const res = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project } });
      setFolderTree(res.data);
    } catch {
      setFolderTree(null);
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...EMPTY_FORM, project: defaultProject ?? '' });
      setSteps([]);
      setFolderPath('');
      setSaveError(null);
      if (initialFolderTree) {
        setFolderTree(initialFolderTree);
      } else if (defaultProject) {
        setFolderTree(null);
        fetchFolderTree(defaultProject);
      } else {
        setFolderTree(null);
      }
    }
  }, [isOpen, defaultProject, initialFolderTree]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'project') {
      setFolderPath('');
      fetchFolderTree(value);
    }
  }

  async function handleCreate() {
    if (!formData.project || !formData.summary.trim()) return;
    if (steps.some(s => !s.description.trim())) {
      setSaveError('All step descriptions are required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const stepsPayload = steps.length
        ? steps.map(s => ({ description: s.description.trim(), expectedResult: s.expectedResult.trim() || undefined }))
        : undefined;
      const dto = {
        project: formData.project,
        summary: formData.summary.trim(),
        description: formData.description || undefined,
        precondition: formData.precondition || undefined,
        automationStatus: formData.automationStatus || undefined,
        testPyramid: formData.testPyramid || undefined,
        type: formData.type || undefined,
        priority: formData.priority || undefined,
        unit: formData.unit || undefined,
        labels: formData.labels.length ? formData.labels : undefined,
        projectType: formData.projectType.length ? formData.projectType : undefined,
        techStack: formData.techStack ? formData.techStack.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        folderPath: effectiveFolderPath || undefined,
        steps: stepsPayload,
      };
      await onCreated(dto);
      onClose();
    } catch (err) {
      console.error('Create test case error:', err);
      setSaveError(httpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const rootChildren = folderTree?.children ?? [];
  const canCreate = !!formData.project && !!formData.summary.trim();
  const effectiveFolderPath = folderPath;

  return (
    <div className="tcd-overlay" onClick={onClose}>
      <div className="tcd-modal" onClick={e => e.stopPropagation()}>

        {/* ── Top bar ── */}
        <div className="tcd-topbar">
          <div className="tcd-topbar-left">
            <h2 className="tcd-topbar-title">New Test Case</h2>
          </div>
          <div className="tcd-topbar-actions">
            <button className="tcd-action-btn tcd-save-btn" onClick={handleCreate} disabled={saving || !canCreate}>
              {saving ? 'Creating…' : 'Create'}
            </button>
            <button className="tcd-action-btn tcd-cancel-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="tcd-close-btn" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {saveError && <p className="tcd-state tcd-error">{saveError}</p>}

        <div className="tcd-layout">

          {/* ── Main column ── */}
          <div className="tcd-main">
            <section className="tcd-section">
              <p className="tcd-section-title">Summary <span className="ctc-required">*</span></p>
              <input
                className="tcd-edit-input"
                value={formData.summary}
                onChange={e => handleChange('summary', e.target.value)}
                placeholder="Test case summary"
              />
            </section>
            <section className="tcd-section">
              <p className="tcd-section-title">Description</p>
              <textarea
                className="tcd-edit-textarea"
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder="Description (optional)"
                rows={4}
              />
            </section>
            <section className="tcd-section">
              <p className="tcd-section-title">Precondition</p>
              <textarea
                className="tcd-edit-textarea"
                value={formData.precondition}
                onChange={e => handleChange('precondition', e.target.value)}
                placeholder="Precondition (optional)"
                rows={4}
              />
            </section>
            <section className="tcd-section">
              <p className="tcd-section-title">Test Steps</p>
              <StepsEditor steps={steps} onChange={setSteps} />
            </section>
          </div>

          {/* ── Sidebar ── */}
          <aside className="tcd-sidebar">
            <div className="tcd-side-field">
              <span className="tcd-side-label">Project <span className="ctc-required">*</span></span>
              {projects?.length ? (
                <select
                  className="tcd-edit-select"
                  value={formData.project}
                  onChange={e => handleChange('project', e.target.value)}
                >
                  <option value="">— select —</option>
                  {projects.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <input
                  className="tcd-edit-input"
                  value={formData.project}
                  onChange={e => handleChange('project', e.target.value)}
                  placeholder="e.g. QA"
                />
              )}
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">
                Folder
                {loadingFolders && <span className="ctc-loading-hint"> loading…</span>}
              </span>
              {formData.project ? (
                <>
                  <FolderPicker
                    rootChildren={rootChildren}
                    onPathChange={setFolderPath}
                  />
                  {effectiveFolderPath && (
                    <span className="ctc-folder-path-preview">
                      {formData.project}/{effectiveFolderPath}
                    </span>
                  )}
                </>
              ) : (
                <span className="ctc-folder-empty">Select a project first.</span>
              )}
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Automation Status</span>
              <select
                className="tcd-edit-select"
                value={formData.automationStatus}
                onChange={e => handleChange('automationStatus', e.target.value)}
              >
                {AUTOMATION_STATUS_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt || '— none —'}</option>
                ))}
              </select>
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Test Pyramid</span>
              <select className="tcd-edit-select" value={formData.testPyramid} onChange={e => handleChange('testPyramid', e.target.value)}>
                <option value="">— none —</option>
                {(fieldOptions.testPyramid ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Priority</span>
              <select className="tcd-edit-select" value={formData.priority} onChange={e => handleChange('priority', e.target.value)}>
                <option value="">— none —</option>
                {(fieldOptions.priority ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Type</span>
              <select className="tcd-edit-select" value={formData.type} onChange={e => handleChange('type', e.target.value)}>
                <option value="">— none —</option>
                {(fieldOptions.type ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Unit</span>
              <select className="tcd-edit-select" value={formData.unit} onChange={e => handleChange('unit', e.target.value)}>
                <option value="">— none —</option>
                {(fieldOptions.unit ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Project Type <span className="ctc-required">*</span></span>
              <LabelsPicker
                selectedLabels={formData.projectType}
                onChange={v => handleChange('projectType', v)}
                availableLabels={PROJECT_TYPE_OPTIONS}
              />
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Tech Stack <span className="ctc-required">*</span></span>
              <input className="tcd-edit-input" value={formData.techStack} onChange={e => handleChange('techStack', e.target.value)} placeholder="Comma-separated, e.g. QT, UX" />
            </div>
            <div className="tcd-side-field">
              <span className="tcd-side-label">Labels</span>
              <LabelsPicker
                selectedLabels={formData.labels}
                onChange={labels => handleChange('labels', labels)}
                availableLabels={availableLabels}
              />
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}
